import {
  useDataContext,
  useFeatureExtractionContext,
  useFxWorkspaceContext,
  type FxWorkspace,
  type FxWorkspaceLoadStatus,
} from '@mobile-mapping/providers';
import { LoadState } from '@trimble/data-loading';
import { Accessor, createMemo } from 'solid-js';
import { UnreachableCaseError } from 'ts-essentials';
import { TreeNodeDefinition } from '@trimble/ui-components/solidjs';

import {
  applyFxReferencesFilter,
  type FxReferencesFilterMode,
} from './filter-fx-reference-nodes';
import {
  flattenFxReferenceNodes,
  type FxReferencesViewMode,
} from './flatten-fx-reference-nodes';
import {
  buildFxWorkspaceNodesForActive,
  FxWorkspaceProjectData,
  FxWorkspaceTreeItem,
} from './fx-workspace-tree-nodes';
import { isProjectDataLoadItemVisible } from './project-data-load-control';
import { isFxMapLayerReference } from './fx-workspace-tree.types';

type ChildrenLoadStatus = 'pending' | 'success' | 'error';

const getFxWorkspacesChildrenLoadStatus = (
  status: FxWorkspaceLoadStatus
): ChildrenLoadStatus => {
  switch (status) {
    case 'idle':
    case 'pending':
      return 'pending';
    case 'error':
      return 'error';
    case 'success':
      return 'success';
    default:
      throw new UnreachableCaseError(status);
  }
};

export interface UseActiveFxWorkspaceTreeOptions {
  referencesFilterMode?: Accessor<FxReferencesFilterMode>;
  referencesViewMode?: Accessor<FxReferencesViewMode>;
}

export interface UseActiveFxWorkspaceTreeResult {
  activeWorkspace: Accessor<FxWorkspace | undefined>;
  nodes: Accessor<TreeNodeDefinition<FxWorkspaceTreeItem>[]>;
  childrenLoadStatus: Accessor<ChildrenLoadStatus>;
}

export function useActiveFxWorkspaceTree(
  options: UseActiveFxWorkspaceTreeOptions = {}
): UseActiveFxWorkspaceTreeResult {
  const fxWorkspace = useFxWorkspaceContext();
  const { dataState } = useDataContext();
  const featureExtraction = useFeatureExtractionContext();

  const projectData = createMemo((): FxWorkspaceProjectData => {
    const missions =
      dataState.mission.state === LoadState.LoadComplete
        ? Object.values(dataState.mission.items)
        : [];
    const trajectories =
      dataState.trajectory.state === LoadState.LoadComplete
        ? Object.values(dataState.trajectory.items)
        : [];
    const models =
      dataState.tcFileFolder.state === LoadState.LoadComplete
        ? Object.values(dataState.tcFileFolder.items)
        : [];
    const pointClouds =
      dataState.pointCloud.state === LoadState.LoadComplete
        ? Object.values(dataState.pointCloud.items)
        : [];
    const showMapWorkspaces =
      featureExtraction.isFeatureExtractionEnabled() &&
      featureExtraction.scope()?.projectId !== undefined;

    return {
      missions,
      trajectories,
      models,
      pointClouds,
      mapWorkspaces: showMapWorkspaces
        ? featureExtraction.editingState.workspaces
        : [],
      mapLayers: showMapWorkspaces ? featureExtraction.editingState.layers : [],
    };
  });

  const activeWorkspace = createMemo(() => {
    const activeWorkspaceId = fxWorkspace.activeWorkspaceId();
    if (activeWorkspaceId === undefined) {
      return undefined;
    }

    return fxWorkspace
      .workspaces()
      .find((workspace) => workspace.id === activeWorkspaceId);
  });

  const nodes = createMemo(() => {
    const workspace = activeWorkspace();
    if (workspace === undefined) {
      return [];
    }

    const tree = buildFxWorkspaceNodesForActive(workspace, projectData(), {
      // Draft (unpublished) breaklines live in the Draft layers panel, not the
      // tree. Until a publish API exists, nothing is published, so the tree's
      // Breaklines folder stays an empty placeholder. Swap [] for the published
      // subset once publish lands.
      getWorkspaceBreaklines: () => [],
      isBreaklinesBuilding: (workspaceId) =>
        fxWorkspace.isBreaklinesBuilding(workspaceId),
    });
    const viewMode = options.referencesViewMode?.() ?? 'tree';
    const viewed = viewMode === 'flat' ? flattenFxReferenceNodes(tree) : tree;
    const filterMode = options.referencesFilterMode?.() ?? 'workspace';
    const draftCounts =
      fxWorkspace.isWorkspaceDataEditing() &&
      fxWorkspace.editingWorkspaceId() === workspace.id;
    const referenceSelected = (refTrn: string): boolean =>
      fxWorkspace.isReferenceVisible(workspace.id, refTrn) ||
      (draftCounts && fxWorkspace.isDraftReference(refTrn));
    const selectedMissionIds = new Set<string>();

    for (const mission of projectData().missions) {
      const missionSelected =
        referenceSelected(mission.missionTrn) ||
        referenceSelected(mission.id) ||
        (mission.pathId != null && referenceSelected(mission.pathId));
      if (missionSelected) {
        selectedMissionIds.add(mission.id);
      }
    }

    return applyFxReferencesFilter(viewed, (node) => {
      if (
        filterMode === 'workspace' &&
        node.data?.missionId !== undefined &&
        selectedMissionIds.has(node.data.missionId) &&
        (node.data.kind === 'referenceMission' ||
          node.data.kind === 'referenceRuns' ||
          (node.data.kind === 'reference' &&
            node.data.loadItemType === 'pointCloud'))
      ) {
        return true;
      }

      if (node.data?.kind !== 'reference' || node.data.refTrn === undefined) {
        return false;
      }

      switch (filterMode) {
        case 'active':
          if (isFxMapLayerReference(node.data)) {
            return featureExtraction.isLayerVisible(node.data.layerId);
          }

          return (
            node.data.loadItemType !== undefined &&
            isProjectDataLoadItemVisible(
              dataState,
              node.data.loadItemType,
              node.data.refTrn
            )
          );
        case 'workspace':
          return referenceSelected(node.data.refTrn);
        default:
          throw new UnreachableCaseError(filterMode);
      }
    });
  });

  const childrenLoadStatus = createMemo(() => {
    const listStatus = getFxWorkspacesChildrenLoadStatus(
      fxWorkspace.loadStatus()
    );
    if (listStatus !== 'success') {
      return listStatus;
    }

    if (fxWorkspace.activeWorkspaceId() === undefined) {
      // Auto-activation of the first workspace happens after the list
      // succeeds. Report pending so the tab shows a spinner instead of empty.
      return fxWorkspace.workspaces().length > 0 ? 'pending' : 'success';
    }

    return getFxWorkspacesChildrenLoadStatus(
      fxWorkspace.activeWorkspaceDetailsLoadStatus()
    );
  });

  return {
    activeWorkspace,
    nodes,
    childrenLoadStatus,
  };
}
